import { LockKeyhole } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { PreferenceControls } from "@/components/app-settings/preference-controls";
import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { buildLoginPayload } from "@/lib/auth/login-form";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

export function LoginPage() {
  const { t } = useAppSettings();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const from = getReturnPath(location.state);

  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const payload = buildLoginPayload({ email, password });

    if (!payload.ok) {
      setError(payload.error);
      return;
    }

    setSubmitting(true);

    try {
      await login(payload.data);
      navigate(from, { replace: true });
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : t("login.failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <PreferenceControls />
      </div>
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <LockKeyhole className="h-4 w-4" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">{t("login.title")}</h1>
            <p className="text-sm text-muted-foreground">quote.vzen.studio</p>
          </div>
        </div>
        <form className="space-y-3" onSubmit={handleSubmit}>
          <Input type="email" placeholder={t("login.email")} autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          <Input
            type="password"
            placeholder={t("login.password")}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}
          <Button className="w-full" type="submit" disabled={submitting || status === "loading"}>
            {submitting ? t("login.signingIn") : t("login.submit")}
          </Button>
        </form>
      </Card>
    </main>
  );
}

function getReturnPath(state: unknown): string {
  if (state && typeof state === "object" && "from" in state) {
    const from = state.from;

    if (from && typeof from === "object" && "pathname" in from && typeof from.pathname === "string") {
      return from.pathname;
    }
  }

  return workbenchRoutes.dashboard;
}
